import styles from './NavBarV2.scss';

import cx from 'classnames';
import React, { forwardRef, useEffect, useRef } from 'react';
import FocusLock from 'react-focus-lock';
import { useHistory } from 'react-router-dom';

import { HasLinkVariant, HasMaterialIcon, LinkVariant, NavigationItem } from 'src/api/contentful/contentTypes.cloverdotcom';
import { LogoLink } from 'src/components/LogoLink/LogoLink';
import { ScrollDirection } from 'src/components/NavBar/NavBarUtil';
import { QA_TEST_PREFIX } from 'src/components/NavTitle/NavTitle';
import { ReferralBanner } from 'src/components/ReferralBanner/ReferralBanner';
import { getReferralType } from 'src/components/SalesChannel/Util';
import { useKeyPress } from 'src/hooks/UseKeyPress';
import { useResizeObserver } from 'src/hooks/UseResizeObserver';
import { useDesktopOrLarge, useMobile, useTablet } from 'src/hooks/UseResponsive';
import { useScrollDirection } from 'src/hooks/UseScrollDirection';
import { isBrowser } from 'src/utils/UserAgent';

import { FlatEntry } from 'src/services/cms/flattenContentful';
import { NavBarDesktop } from './NavBarDesktop/NavBarDesktop';
import { NavBarMobile } from './NavBarMobile/NavBarMobile';
import { NavBarTablet } from './NavBarTablet/NavBarTablet';
import { useUiController } from './UseUiController';
import { useCMSNavigation } from './useCMSNavigation';

export const logo = <div className={styles.navBarLogo}><LogoLink /></div>;

export interface NavBarV2Props extends React.HTMLAttributes<HTMLElement> {
  mainContentRef?: React.RefObject<HTMLDivElement>;
  children?: never;
}

export type NavBarV2Interaction = 'hover' | 'click';

type LinkVariantAndIcon = HasLinkVariant & HasMaterialIcon;

export const NavBarV2 = forwardRef<HTMLElement, NavBarV2Props>(({
  mainContentRef,
  className,
  ...props
}, ref) => {
  const { mainMenu, utilMenu } = useCMSNavigation();
  const menuItems = mainMenu?.items || [];
  const utilItems = (utilMenu?.items || []) as LinkVariantAndIcon[];

  const expandInteraction: NavBarV2Interaction = mainMenu?.interactionOnHover ? 'hover' : 'click';

  const {
    buttonUtilItems,
    textUtilItems,
    materialIconUtilItems,
  } = utilItems.reduce((acc, item) => {
    if (!item) return acc;
    if (item.materialIcon) {
      acc.materialIconUtilItems.push(item);
    } else if (item.variant === LinkVariant.PRIMARY || item.variant === LinkVariant.SECONDARY) {
      acc.buttonUtilItems.push(item);
    } else {
      acc.textUtilItems.push(item);
    }
    return acc;
  }, {
    buttonUtilItems: [] as Array<FlatEntry<NavigationItem>>,
    textUtilItems: [] as Array<FlatEntry<NavigationItem>>,
    materialIconUtilItems: [] as Array<FlatEntry<NavigationItem>>,
  });

  const wrapperGroupRef = useRef<HTMLDivElement>(null);
  const canvasHeightCheckRef = useRef<HTMLDivElement>(null);

  const { current: isReferralTypeBC } = useRef(getReferralType() === 'BC');

  const isMobile = useMobile();
  const isTablet = useTablet();
  const isDesktopOrLarger = useDesktopOrLarge();

  const scrollDirection = useScrollDirection({ disabled: !isReferralTypeBC });
  const isScrollDown = scrollDirection === ScrollDirection.DOWN;

  const uiController = useUiController();
  const { isDrawerOpen, closeDrawer } = uiController;

  // If navigation is changed and drawer is open, close it
  const history = useHistory();
  useEffect(() => {
    const unregisterCallback = history.listen(closeDrawer);
    return () => {
      unregisterCallback();
    };
  }, []);

  useEffect(() => closeDrawer(), [isMobile, isDesktopOrLarger]);

  useEffect(() => {
    if (!isBrowser) return;
    document.body.style.setProperty('--scroll-down', isScrollDown ? '1' : '0');
  }, [isScrollDown]);

  useKeyPress(['Escape'], isDrawerOpen ? closeDrawer : undefined);

  // Observe header height to adjust padding top of main container and set --header-height CSS variable
  useResizeObserver({
    targetRef: wrapperGroupRef, callback: ({ height }) => {
      if (mainContentRef?.current) mainContentRef.current.style.paddingTop = `${height}px`;
      document.body.style.setProperty('--header-height', `${height}px`);
    },
  });

  // Observe canvas height to set drawer height
  useResizeObserver({
    targetRef: canvasHeightCheckRef, callback: ({ height }) => {
      if (wrapperGroupRef.current) wrapperGroupRef.current.style.setProperty('--canvas-height', `${height}px`);
    },
  });

  useEffect(() => {
    return () => {
      if (mainContentRef?.current) mainContentRef.current.style.paddingTop = '';
    };
  }, []);

  // TODO: Redesign release plan
  // - Make default font size 1rem
  // - Show outline style with focus-visible
  useEffect(() => {
    if (!isBrowser) return;

    document.documentElement.classList.add(styles.redesignHtmlStyleOverride);

    return () => document.documentElement.classList.remove(styles.redesignHtmlStyleOverride);
  }, []);

  /**
   * Prevents the overlay from triggering early if the cursor is already on top
   * of the overlay when triggering the expand. Looks at the element that was
   * being hovered prior to hovering on the overlay. If the element is within
   * the navbar hierarchy or is from the Window event, this triggers the closing
   * of the nav drawer.
   *
   * @param {React.MouseEvent} event
   * @returns {void}
   */
  const handleOverlayHoverInteraction = (event: React.MouseEvent): void => {
    if (expandInteraction !== 'hover') return;
    const lastHoverElement: EventTarget | null = event.relatedTarget;
    if (lastHoverElement === window) {
      closeDrawer();
    } else if (lastHoverElement instanceof HTMLElement) {
      const lastElementWithinNavBar = !!lastHoverElement.closest(`.${styles.navBar}`);
      if (lastElementWithinNavBar) closeDrawer();
    }
  };

  return (
    <FocusLock disabled={!isDrawerOpen}>
      <header
        data-test="nav-bar"
        ref={ref}
        className={cx(styles.navBar, className)}
        {...props}
      >
        <div
          ref={wrapperGroupRef}
          className={cx(styles.navBarWrapperGroup, isScrollDown && styles.navBarWrapperGroupHideBanner)}
        >
          {isReferralTypeBC && <ReferralBanner />}
          <div
            data-test={QA_TEST_PREFIX}
            className={styles.navBarWrapper}
            aria-modal={isDrawerOpen || undefined}
            {...props}
          >
            <button
              className={cx(styles.navBarDrawerOverlay, isDrawerOpen && styles.navBarDrawerOverlayShow)}
              aria-hidden="true"
              tabIndex={-1}
              onClick={expandInteraction === 'click' ? closeDrawer : undefined}
              onMouseEnter={handleOverlayHoverInteraction}
            />
            {isMobile && <NavBarMobile {...{ ...uiController, buttonUtilItems, menuItems, textUtilItems, materialIconUtilItems }} />}
            {isTablet && <NavBarTablet {...{ ...uiController, buttonUtilItems, menuItems, textUtilItems, materialIconUtilItems }} />}
            {isDesktopOrLarger && <NavBarDesktop {...{ ...uiController, buttonUtilItems, menuItems, textUtilItems, materialIconUtilItems, expandInteraction }} />}

          </div>
        </div>
        <div className={styles.navBarCanvasHeightCheck} ref={canvasHeightCheckRef} />
      </header>
    </FocusLock>
  );
});
